import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  LayoutDashboard, Briefcase, Users,
  Building2, CreditCard, LogOut,
  ChevronLeft, Menu, Bell, BarChart2, MessageSquare
} from 'lucide-react';

const NAV = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/post-job', icon: Briefcase, label: 'Post a Job' },
  { path: '/applications', icon: Users, label: 'Applications' },
  { path: '/profile', icon: Building2, label: 'Company Profile' },
  { path: '/billing', icon: CreditCard, label: 'Billing & Plan' },
  { path: '/analytics', icon: BarChart2, label: 'Analytics' },
  { path: '/support', icon: MessageSquare, label: 'Support' },
];

export default function Layout({ children }) {
  const { company, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const planColors = {
    trial:      { bg: '#FEF3C7', text: '#92400E', label: 'Free Trial' },
    basic:      { bg: '#DBEAFE', text: '#1E40AF', label: 'Basic' },
    pro:        { bg: '#EDE9FE', text: '#5B21B6', label: 'Pro' },
    enterprise: { bg: '#D1FAE5', text: '#065F46', label: 'Enterprise' },
  };
  const plan = planColors[company?.plan] || planColors.trial;

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>

      {/* ── Sidebar ────────────────────────────────── */}
      <aside style={{
        width: collapsed ? 72 : 260,
        background: '#fff',
        borderRight: '1px solid #E2E8F0',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s ease',
        position: 'fixed',
        top: 0, left: 0, bottom: 0,
        zIndex: 100,
        overflowX: 'hidden',
      }}>

        {/* Logo + collapse button */}
        <div style={{
          padding: collapsed ? '20px 16px' : '20px 24px',
          borderBottom: '1px solid #F1F5F9',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          justifyContent: collapsed ? 'center' : 'space-between',
        }}>
          {!collapsed && (
            <div>
              <div style={{
                fontWeight: 800,
                fontSize: 16,
                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: 1,
              }}>
                UNIVERSE
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
                Careers Portal
              </div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              background: '#F8FAFF',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              padding: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#64748B',
            }}>
            {collapsed ? <Menu size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Company info */}
        {!collapsed && company && (
          <div style={{
            padding: '16px 24px',
            borderBottom: '1px solid #F1F5F9',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              {company.logo_url ? (
                <img
                  src={company.logo_url}
                  alt={company.name}
                  style={{
                    width: 36, height: 36, borderRadius: 8,
                    objectFit: 'cover',
                    border: '1px solid #E2E8F0',
                  }} />
              ) : (
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff', fontWeight: 800, fontSize: 14,
                }}>
                  {company.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontWeight: 700, fontSize: 13, color: '#1E293B',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {company.name}
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  padding: '2px 7px', borderRadius: 4,
                  background: plan.bg, color: plan.text,
                }}>
                  {plan.label}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav style={{
          flex: 1,
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          {NAV.map(({ path, icon: Icon, label }) => {
            const active =
              location.pathname === path ||
              (path === '/post-job' &&
                location.pathname.startsWith('/post-job'));
            return (
              <Link
                key={path}
                to={path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: collapsed ? '10px 0' : '10px 12px',
                  borderRadius: 10,
                  background: active
                    ? 'linear-gradient(135deg,rgba(79,142,247,0.12),rgba(124,58,237,0.08))'
                    : 'transparent',
                  color: active ? '#4F8EF7' : '#64748B',
                  fontWeight: active ? 700 : 500,
                  fontSize: 14,
                  transition: 'all 0.15s',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  textDecoration: 'none',
                }}
                onMouseEnter={e => {
                  if (!active)
                    e.currentTarget.style.background = '#F8FAFF';
                }}
                onMouseLeave={e => {
                  if (!active)
                    e.currentTarget.style.background = 'transparent';
                }}>
                <Icon
                  size={18}
                  strokeWidth={active ? 2.5 : 2} />
                {!collapsed && label}
              </Link>
            );
          })}
        </nav>

        {/* Sign out */}
        <div style={{
          padding: '12px',
          borderTop: '1px solid #F1F5F9',
        }}>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: collapsed ? '10px 0' : '10px 12px',
              borderRadius: 10,
              background: 'transparent',
              border: 'none',
              color: '#EF4444',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              width: '100%',
              justifyContent: collapsed ? 'center' : 'flex-start',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e =>
              e.currentTarget.style.background = '#FFF1F1'}
            onMouseLeave={e =>
              e.currentTarget.style.background = 'transparent'}>
            <LogOut size={18} />
            {!collapsed && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* ── Main content ───────────────────────────── */}
      <main style={{
        marginLeft: collapsed ? 72 : 260,
        flex: 1,
        transition: 'margin-left 0.25s ease',
        minHeight: '100vh',
        background: '#F8FAFF',
      }}>
        {/* Top bar */}
        <div style={{
          background: '#fff',
          borderBottom: '1px solid #E2E8F0',
          padding: '14px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 50,
        }}>
          <div style={{ fontSize: 13, color: '#94A3B8' }}>
            careers.inncatechnologies.com
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <button style={{
              background: '#F8FAFF',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              padding: '6px 8px',
              cursor: 'pointer',
              color: '#64748B',
              display: 'flex',
              alignItems: 'center',
            }}>
              <Bell size={16} />
            </button>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center',
              color: '#fff', fontWeight: 800, fontSize: 12,
              cursor: 'pointer',
            }}>
              {company?.name?.[0]?.toUpperCase() || 'C'}
            </div>
          </div>
        </div>

        {/* Page content */}
        <div style={{ padding: '32px' }}>
          {children}
        </div>
      </main>
    </div>
  );
}